/**
 * 增强版批量操作组件
 * 整合了原有的批量操作、V3.3 产品驱动批量操作和新的 V3.0 明细表批量操作
 */

import React, { useState, useMemo } from "react";
import { Button, Dropdown, Space, Divider, Tooltip, Typography, theme } from "antd";
import { DownOutlined, ThunderboltOutlined, ShopOutlined, ExperimentOutlined, MailOutlined, CopyOutlined, CloseOutlined, CheckSquareOutlined, AppstoreOutlined } from "@ant-design/icons";
import AogNoticeAction from "./AogNoticeAction";
import BatchTransactionActions from "./BatchTransactionActions";
import { GenericEmailSendDrawer } from "../../../../components/EmailSender";

const { Text } = Typography;

/**
 * 增强版批量操作组件
 * 整合了所有的批量操作功能，包括选择管理、复制、事务处理和邮件发送
 */
const EnhancedBatchActions = ({
    selectedFlightIds = [],
    onSuccess,
    allFlightsData = [],
    onOpenBatchTransactionDrawer,
    onOpenBatchTransactionDrawerV3, // V3.0版本的回调
    onFlightRemove,
    // 新增 props 用于集成选择和复制功能
    onSelectAll,
    onClearSelection,
    copyMenuItems,
}) => {
    const { token } = theme.useToken();

    // 通用邮件发送抽屉状态
    const [emailDrawerVisible, setEmailDrawerVisible] = useState(false);

    // 获取选中的航班数据
    const selectedFlightsData = useMemo(() => {
        return allFlightsData.filter((flight) => selectedFlightIds.includes(flight.id));
    }, [allFlightsData, selectedFlightIds]);

    // V3.3 产品驱动的批量操作菜单项
    const v3BatchActionItems = [
        {
            key: "PERMIT_REQUEST",
            label: (
                <Space>
                    <ShopOutlined style={{ color: "#1890ff" }} />
                    <span>批量许可申请</span>
                </Space>
            ),
            onClick: () => onOpenBatchTransactionDrawer("PERMIT_REQUEST"),
        },
        {
            key: "CREW_TRANSPORTATION",
            label: (
                <Space>
                    <ShopOutlined style={{ color: "#722ed1" }} />
                    <span>批量机组接送</span>
                </Space>
            ),
            onClick: () => onOpenBatchTransactionDrawer("CREW_TRANSPORTATION"),
        },
        {
            key: "HOTAC",
            label: (
                <Space>
                    <ShopOutlined style={{ color: "#eb2f96" }} />
                    <span>批量酒店住宿</span>
                </Space>
            ),
            onClick: () => onOpenBatchTransactionDrawer("HOTAC"),
        },
    ];

    // V3.0 明细表驱动的批量操作菜单项
    const v3DetailActionItems = [
        {
            key: "PERMIT_REQUEST_V3",
            label: (
                <Space align="start">
                    <ExperimentOutlined style={{ color: "#1890ff", marginTop: 4 }} />
                    <div>
                        <div>批量许可申请 (V3.0)</div>
                        <div style={{ fontSize: 11, color: "#999" }}>明细表与子类型管理</div>
                    </div>
                </Space>
            ),
            onClick: () => onOpenBatchTransactionDrawerV3 && onOpenBatchTransactionDrawerV3("PERMIT_REQUEST"),
        },
        {
            key: "FUEL_REQUEST_V3",
            label: (
                <Space align="start">
                    <ExperimentOutlined style={{ color: "#52c41a", marginTop: 4 }} />
                    <div>
                        <div>批量燃油申请 (V3.0)</div>
                        <div style={{ fontSize: 11, color: "#999" }}>明细表与产品管理</div>
                    </div>
                </Space>
            ),
            onClick: () => onOpenBatchTransactionDrawerV3 && onOpenBatchTransactionDrawerV3("FUEL_REQUEST"),
        },
    ];

    // 处理邮件发送成功
    const handleEmailSuccess = () => {
        setEmailDrawerVisible(false);
        selectedFlightIds.forEach((flightId) => {
            onSuccess?.(flightId);
        });
    };

    if (selectedFlightIds.length === 0) {
        return null;
    }

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                background: "linear-gradient(135deg, rgba(250, 252, 255, 0.98) 0%, rgba(245, 248, 255, 0.98) 100%)",
                backdropFilter: "blur(12px)",
                padding: "10px 20px",
                borderRadius: "10px",
                boxShadow: "0 6px 20px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)",
                border: "1px solid rgba(24, 144, 255, 0.15)",
                gap: 16,
                flexWrap: "wrap",
            }}
        >
            {/* 第一部分：选择状态与管理 */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        lineHeight: 1.2,
                        borderRight: "1px solid rgba(0,0,0,0.06)",
                        paddingRight: 16,
                    }}
                >
                    <Text style={{ fontSize: 12, color: "#888" }}>已选择</Text>
                    <Text style={{ fontSize: 16, fontWeight: 600, color: token.colorPrimary }}>
                        {selectedFlightIds.length} <span style={{ fontSize: 12, fontWeight: 400, color: "#666" }}>航班</span>
                    </Text>
                </div>

                {(onSelectAll || onClearSelection) && (
                    <Space size={4}>
                        {onSelectAll && (
                            <Tooltip title="选中当前页所有航班">
                                <Button type="text" size="small" icon={<CheckSquareOutlined />} onClick={onSelectAll}>
                                    全选
                                </Button>
                            </Tooltip>
                        )}
                        {onClearSelection && (
                            <Tooltip title="取消当前所有选择">
                                <Button type="text" size="small" danger icon={<CloseOutlined />} onClick={onClearSelection}>
                                    取消
                                </Button>
                            </Tooltip>
                        )}
                    </Space>
                )}
            </div>

            {/* 第二部分：基础工具 (复制) */}
            {copyMenuItems && (
                <>
                    <Divider type="vertical" style={{ height: 24, margin: 0 }} />
                    <Dropdown menu={{ items: copyMenuItems }} placement="bottom" trigger={["click"]}>
                        <Button type="text" icon={<CopyOutlined />}>
                            复制 <DownOutlined style={{ fontSize: 10, marginLeft: 2, color: "#999" }} />
                        </Button>
                    </Dropdown>
                </>
            )}

            {/* 第三部分：核心业务 (BatchTransactionActions) */}
            <Divider type="vertical" style={{ height: 24, margin: 0 }} />
            <div style={{ display: "flex", alignItems: "center" }}>
                <BatchTransactionActions selectedFlightIds={selectedFlightIds} onSuccess={onSuccess} allFlightsData={allFlightsData} onFlightRemove={onFlightRemove} onOpenBatchTransactionDrawer={onOpenBatchTransactionDrawer} />
            </div>

            {/* 第四部分：高级/扩展业务 */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
                {/* V4 产品驱动操作 */}
                <Dropdown menu={{ items: v3BatchActionItems }} trigger={["click"]}>
                    <Button
                        size="small"
                        icon={<AppstoreOutlined />}
                        style={{
                            borderColor: "#1890ff",
                            color: "#1890ff",
                            background: "transparent",
                        }}
                    >
                        更多事务 <DownOutlined style={{ fontSize: 10, marginLeft: 2 }} />
                    </Button>
                </Dropdown>

                {/* V3.0 明细操作 */}
                {onOpenBatchTransactionDrawerV3 && (
                    <Dropdown menu={{ items: v3DetailActionItems }} trigger={["click"]}>
                        <Button
                            size="small"
                            icon={<ExperimentOutlined />}
                            style={{
                                borderColor: "#722ed1",
                                color: "#722ed1",
                                background: "transparent",
                            }}
                        >
                            V3操作 <DownOutlined style={{ fontSize: 10, marginLeft: 2 }} />
                        </Button>
                    </Dropdown>
                )}

                <AogNoticeAction flights={selectedFlightsData} selectedCount={selectedFlightIds.length} />

                {/* 邮件发送 */}
                <Tooltip title="发送通用邮件">
                    <Button
                        type="primary"
                        size="small"
                        icon={<MailOutlined />}
                        onClick={() => setEmailDrawerVisible(true)}
                        style={{
                            background: "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                            borderColor: "#389e0d",
                            boxShadow: "0 2px 4px rgba(82, 196, 26, 0.2)",
                        }}
                    >
                        发送邮件
                    </Button>
                </Tooltip>
            </div>

            {/* 隐藏的抽屉组件 */}
            <GenericEmailSendDrawer visible={emailDrawerVisible} onClose={() => setEmailDrawerVisible(false)} onSuccess={handleEmailSuccess} presetData={selectedFlightsData} dataType="FLIGHT_OPERATION" templateFilter={{ category: "FLIGHT_OPERATION,PERMIT_REQUEST,FUEL_REQUEST" }} autoOpenEditor={true} />
        </div>
    );
};

export default EnhancedBatchActions;
